import { useState } from 'react';
import { Pagination } from 'antd';

import { useLetterhead } from '@/api/bookings';
import { useGoodsReceipt, useGoodsReceipts, type GoodsReceiptSummary } from '@/api/goodsReceipts';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { IconAction } from '@/components/IconAction';
import { Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { useDebounced } from '@/components/useDebounced';
import { formatDocumentDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { can } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { GoodsReceiptSheet } from './print/GoodsReceiptSheet';
import { PrintFrame } from './print/PrintFrame';

const PAGE_SIZE = 25;

/** A goods receipt is filed paper: a sheet, never a roll. */
const receiptPapers = ['A5', 'A4'] as const;

/**
 * The goods receipt register (spec §16, step 1 of `docs/Purchase_Sale_Outstanding_Design.md`) —
 * every delivery booked in at this branch, newest first, each printable as its receipt note.
 *
 * <p>
 * <strong>Read-only, and there is no "new receipt" button.</strong> A receipt is written by Book in
 * stock on Item units, as the garments are; a receipt made here would be paper for stock that never
 * reached the rail. The cost column is behind `GoodsReceipt:ViewFinancials`, which wardrobe staff —
 * who "deliberately cannot see money" — do not hold: they see what came in, not what it cost.
 * </p>
 */
export function GoodsReceiptsPage({ session }: { session: Session }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [printing, setPrinting] = useState<number | null>(null);

  const debouncedSearch = useDebounced(search);
  const branchId = session.branch?.id;
  const receipts = useGoodsReceipts(branchId, {
    search: debouncedSearch || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const letterhead = useLetterhead(branchId);
  const open = useGoodsReceipt(printing);
  const mayPrint = can(session, 'GoodsReceipt:Print');
  const maySeeCost = can(session, 'GoodsReceipt:ViewFinancials');

  const total = receipts.data?.total ?? 0;

  if (printing !== null && open.data) {
    return (
      <PrintFrame
        onClose={() => setPrinting(null)}
        title={`Goods receipt ${open.data.documentNumber}`}
        sizes={receiptPapers}
        remember="goods-receipt"
      >
        {(paper) => (
          <GoodsReceiptSheet
            paper={paper}
            receipt={open.data}
            letterhead={letterhead.data}
            showCost={maySeeCost}
          />
        )}
      </PrintFrame>
    );
  }

  const columns: DataViewColumn<GoodsReceiptSummary>[] = [
    {
      title: 'Receipt',
      dataIndex: 'documentNumber',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="font-mono-nums text-sm font-medium text-gray-900">
            {row.documentNumber}
          </div>
          <div className="text-[13px] text-default">{formatDocumentDate(row.receivedOn)}</div>
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      render: (_, row) => (
        <div>
          <div className="text-[13px] text-dark">{row.supplierName ?? '—'}</div>
          <div className="font-mono-nums text-[11px] text-default">
            {row.supplierReference ?? 'No challan number'}
          </div>
        </div>
      ),
    },
    {
      title: 'Garments',
      dataIndex: 'totalUnits',
      render: (_, row) => <span className="text-[13px] text-dark">{row.totalUnits}</span>,
    },
    ...(maySeeCost
      ? [
          {
            title: 'Cost',
            dataIndex: 'totalCost',
            hideOnCard: true,
            render: (_: unknown, row: GoodsReceiptSummary) => (
              <span className="font-mono-nums text-[13px] text-dark">
                {row.totalCost === null ? '—' : formatAmount(row.totalCost)}
              </span>
            ),
          },
        ]
      : []),
    ...(mayPrint
      ? [
          {
            title: '',
            dataIndex: 'id',
            trailing: true,
            render: (_: unknown, row: GoodsReceiptSummary) => (
              <IconAction
                icon="icon icon-printer"
                label={`Print ${row.documentNumber}`}
                onClick={() => setPrinting(row.id)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeading
        title="Goods receipts"
        subtitle="Every delivery booked in at this branch. A receipt is written by Book in stock on Item units — nothing is created here."
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-border-color pb-3">
          <input
            className="min-w-56 flex-1 rounded-md border border-border-color bg-white px-3 py-1.5 text-[13px]"
            placeholder="Receipt number or the supplier's challan"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <DataView<GoodsReceiptSummary>
          columns={columns}
          dataSource={receipts.data?.items ?? []}
          loading={receipts.isLoading}
          empty={
            <EmptyState
              icon="icon icon-package"
              title={
                debouncedSearch ? 'Nothing matches that' : 'No delivery has been booked in yet'
              }
              hint={
                debouncedSearch
                  ? 'Clear the search.'
                  : 'Book in stock on Item units writes a goods receipt for every delivery.'
              }
            />
          }
        />

        {total > PAGE_SIZE && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} receipts`}
            />
          </div>
        )}
      </Card>
    </>
  );
}
